import { useCallback, useMemo } from 'react';
import { useWindowDimensions } from 'react-native';
import { useShallow } from 'zustand/react/shallow';
import { useTabWorkspaceLayout } from '../../../shared/layout/useContentLayout';
import { POS_MOBILE_CART_COMPACT_LAYOUT } from '../../application/posLayout.constants'
import { usePosRemoteSaleDraft } from '../../application/usePosRemoteSaleDraft';
import { usePosCartPromotions } from '../../application/usePosCartPromotions';
import { usePosStore } from '../../store/usePosStore';

type Params = {
  currentBranchId: string | null | undefined;
  organizationId: string | null;
  sessionUserId: string | null | undefined;
  canEdit: boolean;
  canDiscount?: boolean;
};

export function usePosCartPane({
  currentBranchId,
  organizationId,
  sessionUserId,
  canEdit,
  canDiscount = true,
}: Params) {
  const { height } = useWindowDimensions();
  const { layoutWide: isWidePosLayout } = useTabWorkspaceLayout();

  const {
    cart,
    removeFromCart,
    incrementCartItem,
    setCartLineQuantity,
    removeCartLine,
    clearCart,
    setCartLineAttribution,
    setCartLineNote,
  } = usePosStore(
    useShallow((s) => ({
      cart: s.cart,
      removeFromCart: s.removeFromCart,
      incrementCartItem: s.incrementCartItem,
      setCartLineQuantity: s.setCartLineQuantity,
      removeCartLine: s.removeCartLine,
      clearCart: s.clearCart,
      setCartLineAttribution: s.setCartLineAttribution,
      setCartLineNote: s.setCartLineNote,
    }))
  );

  const {
    evaluation: cartEvaluation,
    showPromoActions,
    hasManualCodePromotions,
    hasCashierSelectPromotions,
    promoCodeInput,
    setPromoCodeInput,
    appliedPromoCode,
    appliedPromoCodes,
    setAppliedPromoCode,
    appliedManualPromotionId,
    appliedManualPromotionIds,
    promoCodeError,
    applyPromoCode,
    clearAppliedPromoCode,
    selectedCashierPromotionId,
    selectCashierPromotion,
    cashierEligiblePromotions,
    inactiveCashierPromoHints,
    clearPromoSelection,
    promoUiStatus,
    reloadPromotionsFromLocal,
    reconcileStalePromoAfterRestore,
    discretionaryDiscountTotal,
    resolveEvaluationForCheckout,
    isApplying,
    isClearing,
  } = usePosCartPromotions(cart, currentBranchId, canDiscount);

  const cartPersistenceScope = useMemo(() => {
    if (!organizationId || !currentBranchId || !sessionUserId) return null;
    return {
      organizationId,
      branchId: currentBranchId,
      userId: sessionUserId,
    };
  }, [organizationId, currentBranchId, sessionUserId]);

  const onRestoreCartPromo = useCallback(
    (promo: { appliedPromoCode: string | null; selectedCashierPromotionId: string | null }) => {
      setAppliedPromoCode(promo.appliedPromoCode);
      setPromoCodeInput(promo.appliedPromoCode ?? '');
      selectCashierPromotion(promo.selectedCashierPromotionId);
      if (!promo.appliedPromoCode && !promo.selectedCashierPromotionId) {
        clearPromoSelection();
      }
    },
    [clearPromoSelection, selectCashierPromotion, setAppliedPromoCode, setPromoCodeInput]
  );

  const handleCartSnapshotRestored = useCallback(() => {
    reconcileStalePromoAfterRestore();
  }, [reconcileStalePromoAfterRestore]);

  const {
    hasParkedSale,
    parkedSummary,
    activeDraftId,
    canParkCurrentSale,
    parkCurrentSale,
    resumeParkedSale,
    discardParkedSale,
    clearPersistenceAfterSale,
    ConfirmModal,
  } = usePosRemoteSaleDraft({
    scope: cartPersistenceScope,
    canEdit,
    promo: {
      appliedPromoCode,
      selectedCashierPromotionId,
    },
    onRestorePromo: onRestoreCartPromo,
    onCartSnapshotRestored: handleCartSnapshotRestored,
  });

  const posCartLinesMaxHeight = useMemo(() => {
    if (isWidePosLayout) {
      return Math.min(height * 0.46, 420);
    }
    if (POS_MOBILE_CART_COMPACT_LAYOUT) {
      return Math.min(Math.max(height * 0.22, 100), 180);
    }
    return Math.min(Math.max(height * 0.34, 160), 280);
  }, [height, isWidePosLayout]);

  const saleItems = useMemo(
    () =>
      cart.map((item) => ({
        productId: item.productId,
        variantId: item.variantId ?? null,
        quantity: item.quantity,
        price: item.price,
        name: item.name,
        sku: item.sku,
        itemType: item.itemType,
        affectsStock: item.affectsStock,
        unitOfMeasure: item.unitOfMeasure ?? null,
        options: item.menuOptions?.length
          ? item.menuOptions.map((o) => ({
              groupId: o.groupId,
              groupName: o.groupName,
              choiceId: o.choiceId,
              choiceName: o.choiceName,
              priceDelta: o.priceDelta,
              qty: o.qty,
              linkedProductId: o.linkedProductId ?? null,
              modifierKind: o.modifierKind ?? null,
              allergenTags: o.allergenTags ?? [],
              kitchenLabel: o.kitchenLabel ?? null,
              substitutesChoiceId: o.substitutesChoiceId ?? null,
            }))
          : null,
        performedByUserId: item.performedByUserId ?? null,
        soldByUserId: item.soldByUserId ?? null,
      })),
    [cart]
  );

  return {
    cart,
    clearCart,
    removeFromCart,
    incrementCartItem,
    setCartLineQuantity,
    removeCartLine,
    setCartLineAttribution,
    setCartLineNote,
    cartEvaluation,
    saleItems,
    posCartLinesMaxHeight,
    showPromoActions,
    hasManualCodePromotions,
    hasCashierSelectPromotions,
    discretionaryDiscountTotal,
    promoCodeInput,
    setPromoCodeInput,
    appliedPromoCode,
    appliedPromoCodes,
    appliedManualPromotionId,
    appliedManualPromotionIds,
    promoCodeError,
    applyPromoCode,
    clearAppliedPromoCode,
    selectedCashierPromotionId,
    cashierEligiblePromotions,
    inactiveCashierPromoHints,
    selectCashierPromotion,
    promoUiStatus,
    clearPromoSelection,
    reloadPromotionsFromLocal,
    resolveEvaluationForCheckout,
    isApplying,
    isClearing,
    canParkCurrentSale,
    hasParkedSale,
    parkedSummary,
    activeDraftId,
    parkCurrentSale,
    resumeParkedSale,
    discardParkedSale,
    clearPersistenceAfterSale,
    ConfirmModal,
  };
}
